import chunk from 'lodash/chunk'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'

import type CustomPeriodForm from 'modules/attendance/components/TimeTracking/Layout/form'
import attendanceCustomPeriods from 'modules/attendance/store/collections/customPeriods'
import employeeStats from 'modules/attendance/store/collections/employeeStats'

const DEFAULT_BATCH_SIZE = 35

interface Filters {
  start_on: string
  end_on: string
}

interface Data extends Filters {
  employee_ids: string[]
}

const dataInBatches = (
  ids: string[],
  idsField: 'employee_ids',
  filters: Filters,
  batchSize: number
) => {
  return chunk(ids, batchSize).map((group: string[], i: number) => {
    const data: Data = { ...filters, employee_ids: [] }
    data[idsField] = group

    if (i === 0) {
      return {
        data: data,
      }
    }

    return {
      data: data,
      add: true,
      remove: false,
    }
  })
}

export default function useFetchCustomPeriods(
  customPeriodForm: CustomPeriodForm,
  periodType: string,
  hasTimeTrackingCollection: boolean
) {
  const startOn = dateToString(customPeriodForm.startOn, 'backend')
  const endOn = dateToString(customPeriodForm.endOn, 'backend')

  const startAndEndDates = {
    start_on: startOn,
    end_on: endOn,
  }

  const dependencies = [
    customPeriodForm.startOn.month() + 1,
    customPeriodForm.startOn.year(),
    customPeriodForm.startOn.date(),
    periodType,
  ]

  const { loading } = useAsyncData(async () => {
    if (hasTimeTrackingCollection) return Promise.resolve()

    if (hasFeature('time_tracking_page_with_pagination')) {
      const statsModel = await employeeStats.fetch({
        data: startAndEndDates,
      })

      if (statsModel && !employeeStats.isEmpty) {
        const dataBatches = dataInBatches(
          employeeStats.employeeIds,
          'employee_ids',
          startAndEndDates,
          DEFAULT_BATCH_SIZE
        )

        const initialBatch = dataBatches.shift()
        const initialData = await attendanceCustomPeriods.fetch(initialBatch)

        if (initialData) {
          Promise.all(
            dataBatches.map((dataBatch) => {
              return attendanceCustomPeriods.fetch(dataBatch)
            })
          )
        }
      }
    } else {
      await attendanceCustomPeriods.fetch({
        data: {
          year: customPeriodForm.startOn.year(),
          month: customPeriodForm.startOn.month() + 1,
          period_type: customPeriodForm.periodType,
          ...startAndEndDates,
        },
      })
    }
    track(trackingEvents.timeTracking.selectPeriodType, {
      period_type: customPeriodForm.periodType,
    })
  }, dependencies)

  return {
    loading,
  }
}
